import React, { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Building2, Layers, Wrench, Globe, Crosshair, X } from 'lucide-react';
import { tesisTuruList } from '../../utils/tesisTuru';

export const EMPTY_SCOPE = { mode: 'filo', tesis_turu: '', ekipman_tipi: '', bina_id: '', equipment_id: '' };

const trig = 'bg-gray-800 border-gray-700 text-white h-8 text-xs';

/** Uzman Asistan kapsam seçici: Genel soru ↔ Filo/Ekipman (tesis türü · ekipman tipi · bina · tek ekipman) */
export function SohbetKapsam({ scope, onChange, options, disabled }) {
  const isGenel = scope.mode === 'genel';
  const set = (patch) => onChange({ ...scope, ...patch });

  const binalar = useMemo(() => (options?.binalar || []).filter(b => !scope.tesis_turu || tesisTuruList(b.tesis_turu).includes(scope.tesis_turu)), [options, scope.tesis_turu]);
  const ekipmanlar = useMemo(() => (options?.ekipmanlar || []).filter(e =>
    (!scope.ekipman_tipi || e.ekipman_tipi === scope.ekipman_tipi) &&
    (!scope.bina_id || e.bina_id === scope.bina_id) &&
    (!scope.tesis_turu || binalar.some(b => b.id === e.bina_id))
  ), [options, scope.ekipman_tipi, scope.bina_id, scope.tesis_turu, binalar]);
  const binaAdi = (id) => (options?.binalar || []).find(b => b.id === id)?.bina_adi || '';
  const aktif = ['tesis_turu', 'ekipman_tipi', 'bina_id', 'equipment_id'].filter(k => scope[k]).length;

  return (
    <div className="px-3 py-2 border-b border-gray-800 bg-gray-900/40 space-y-2" data-testid="ez-kapsam">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-gray-700 overflow-hidden text-xs">
          <button onClick={() => set({ mode: 'filo' })} disabled={disabled} className={`px-3 py-1.5 flex items-center gap-1 ${!isGenel ? 'bg-[#E67E22] text-white' : 'bg-gray-800 text-gray-300 hover:text-white'}`} data-testid="ez-kapsam-mod-filo"><Crosshair className="w-3.5 h-3.5" /> Filo / Ekipman</button>
          <button onClick={() => set({ mode: 'genel' })} disabled={disabled} className={`px-3 py-1.5 flex items-center gap-1 ${isGenel ? 'bg-cyan-700 text-white' : 'bg-gray-800 text-gray-300 hover:text-white'}`} data-testid="ez-kapsam-mod-genel"><Globe className="w-3.5 h-3.5" /> Genel Soru</button>
        </div>
        {isGenel ? (
          <span className="text-[11px] text-cyan-300" data-testid="ez-kapsam-ozet">Filo verisi bağlanmaz — mevzuat, standart, periyot ve bakım pratikleri hakkında serbest soru sorun.</span>
        ) : (
          <>
            <span className="text-[11px] text-gray-400" data-testid="ez-kapsam-ozet">
              {aktif === 0 ? 'Kapsam: tüm filo' : `Kapsam: ${[
                scope.tesis_turu && `Tesis: ${options?.tesis_turleri?.find(t => t.key === scope.tesis_turu)?.label || scope.tesis_turu}`,
                scope.ekipman_tipi && `Tip: ${options?.ekipman_tipleri?.find(t => t.key === scope.ekipman_tipi)?.label || scope.ekipman_tipi}`,
                scope.bina_id && `Bina: ${binaAdi(scope.bina_id)}`,
                scope.equipment_id && `Ekipman: ${(options?.ekipmanlar || []).find(e => e.id === scope.equipment_id)?.seri_no || ''}`,
              ].filter(Boolean).join(' · ')}`}
            </span>
            {aktif > 0 && <button onClick={() => onChange({ ...EMPTY_SCOPE })} className="text-[11px] text-gray-500 hover:text-white flex items-center gap-0.5" data-testid="ez-kapsam-temizle"><X className="w-3 h-3" /> Temizle</button>}
          </>
        )}
      </div>
      {!isGenel && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <Select value={scope.tesis_turu || 'all'} onValueChange={v => set({ tesis_turu: v === 'all' ? '' : v, bina_id: '', equipment_id: '' })} disabled={disabled}>
            <SelectTrigger className={trig} data-testid="ez-kapsam-tesis"><Layers className="w-3.5 h-3.5 mr-1 text-gray-400" /><SelectValue placeholder="Tesis türü" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              <SelectItem value="all">Tüm tesis türleri</SelectItem>
              {(options?.tesis_turleri || []).map(t => <SelectItem key={t.key} value={t.key}>{t.label} ({t.bina_sayisi} bina)</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scope.ekipman_tipi || 'all'} onValueChange={v => set({ ekipman_tipi: v === 'all' ? '' : v, equipment_id: '' })} disabled={disabled}>
            <SelectTrigger className={trig} data-testid="ez-kapsam-tip"><Wrench className="w-3.5 h-3.5 mr-1 text-gray-400" /><SelectValue placeholder="Ekipman tipi" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-72">
              <SelectItem value="all">Tüm ekipman tipleri</SelectItem>
              {(options?.ekipman_tipleri || []).map(t => <SelectItem key={t.key} value={t.key}>{t.label} ({t.adet})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scope.bina_id || 'all'} onValueChange={v => set({ bina_id: v === 'all' ? '' : v, equipment_id: '' })} disabled={disabled}>
            <SelectTrigger className={trig} data-testid="ez-kapsam-bina"><Building2 className="w-3.5 h-3.5 mr-1 text-gray-400" /><SelectValue placeholder="Bina" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-72">
              <SelectItem value="all">Tüm binalar</SelectItem>
              {binalar.map(b => <SelectItem key={b.id} value={b.id}>{b.bina_adi}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scope.equipment_id || 'all'} onValueChange={v => set({ equipment_id: v === 'all' ? '' : v })} disabled={disabled}>
            <SelectTrigger className={`${trig} ${scope.equipment_id ? 'border-[#E67E22]/60' : ''}`} data-testid="ez-kapsam-ekipman"><Crosshair className="w-3.5 h-3.5 mr-1 text-gray-400" /><SelectValue placeholder="Belirli ekipman" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-72">
              <SelectItem value="all">Belirli ekipman seçilmedi ({ekipmanlar.length})</SelectItem>
              {ekipmanlar.map(e => (
                <SelectItem key={e.id} value={e.id}>
                  <span className="font-mono text-[11px] text-blue-300 mr-1">{e.seri_no}</span>{e.alt_tip || e.ekipman_tipi} · {e.marka} {e.model} · {binaAdi(e.bina_id)}
                  {e.ai_health_score != null && <Badge className="ml-1 text-[9px] px-1 py-0 bg-gray-700 text-gray-200">{e.ai_health_score}</Badge>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

export default SohbetKapsam;
